import { useCallback, useEffect } from 'react';

import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import {
  allFilters,
  defaultQueryParamKeys,
  DateRangeFilter,
  RangePickerQueryParamKeys,
  determineCorrectDateFromPresetOrRange,
  TimeRangeConstraints,
} from '@cast/shared-date-ui';

type UseTimeRangeSelectionProps = {
  queryParamKeys?: RangePickerQueryParamKeys;
  initialState: {
    preset: DateRangeFilter;
    timeZone?: string;
  };
  constraints?: TimeRangeConstraints;
  allowTimeInput?: boolean;
};

export const useTimeRangeSelection = ({
  queryParamKeys = defaultQueryParamKeys,
  initialState,
  constraints,
  allowTimeInput = false,
}: UseTimeRangeSelectionProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const from = searchParams.get(queryParamKeys.fromDate) || undefined;
  const to = searchParams.get(queryParamKeys.toDate) || undefined;
  const preset = searchParams.get(queryParamKeys.dateRangePreset) || undefined;
  const timeZone =
    searchParams.get(queryParamKeys.timeZone) ||
    initialState.timeZone ||
    dayjs.tz.guess();

  const getRange = useCallback(() => {
    return determineCorrectDateFromPresetOrRange({
      preset: preset as DateRangeFilter | undefined,
      timezone: timeZone,
      from,
      to,
      fallbackPreset: initialState.preset,
      allowTimeInput,
      constraints,
    });
  }, [
    preset,
    from,
    to,
    initialState.preset,
    timeZone,
    constraints,
    allowTimeInput,
  ]);

  // Remove invalid preset query param
  useEffect(() => {
    if (preset) {
      const presetFilter = allFilters[preset as keyof typeof allFilters];
      if (!presetFilter) {
        searchParams.delete(queryParamKeys.dateRangePreset);
        setSearchParams(
          (prev) => {
            prev.delete(queryParamKeys.dateRangePreset);
            return prev;
          },
          { replace: true }
        );
      }
    }
  }, [preset, queryParamKeys.dateRangePreset, searchParams, setSearchParams]);

  return {
    from,
    to,
    preset: preset as DateRangeFilter | undefined,
    timeZone,
    getRange,
    key: `${from}-${to}-${preset}-${timeZone}`,
    initialState,
    constraints,
  };
};
